/**
 * utils/documentActions.ts
 * Which actions the document view offers, by document type and live status.
 *
 * Mirrors what the server will accept, so a button is never shown for a call
 * that can only fail: void needs an ISSUED document with nothing settled,
 * write-off needs an open receivable with a balance, a quote converts from
 * ISSUED or ACCEPTED. Void and write-off are ADMIN-only.
 */

export interface DocAction {
  act: 'record-payment' | 'send' | 'payment-link' | 'credit-note' | 'write-off' | 'void' | 'convert' | 'accept' | 'decline';
  label: string;
  icon: string;
  primary?: boolean;
  danger?: boolean;
}

export const OPEN_STATUSES = ['ISSUED', 'PARTIALLY_PAID', 'OVERDUE'];

export function documentActions(input: {
  type: string;
  status: string;
  /** Still owed on the document. */
  balance: number;
  /** Already settled against it (payments, credits). */
  settled: number;
  admin: boolean;
}): DocAction[] {
  const { type, status, balance, settled, admin } = input;
  const open = OPEN_STATUSES.includes(status);
  const list: DocAction[] = [];

  if (type === 'QUOTE') {
    if (status === 'ISSUED' || status === 'ACCEPTED') list.push({ act: 'convert', label: 'Convert to invoice', icon: 'fileText', primary: true });
    if (status === 'ISSUED') {
      list.push({ act: 'accept', label: 'Mark accepted', icon: 'check' }, { act: 'decline', label: 'Mark declined', icon: 'ban', danger: true });
    }
    if (status === 'ISSUED' || status === 'ACCEPTED') list.push({ act: 'send', label: 'Send', icon: 'send' });
  } else if (type === 'INVOICE') {
    if (open && balance > 0) list.push({ act: 'record-payment', label: 'Record payment', icon: 'wallet', primary: true });
    if (status !== 'VOID') list.push({ act: 'send', label: 'Send', icon: 'send' });
    if (open && balance > 0) list.push({ act: 'payment-link', label: 'Payment link', icon: 'link' });
    if (['ISSUED', 'PARTIALLY_PAID', 'PAID', 'OVERDUE'].includes(status)) list.push({ act: 'credit-note', label: 'Credit note', icon: 'creditNote' });
    if (admin && (status === 'ISSUED' || status === 'PARTIALLY_PAID') && balance > 0) {
      list.push({ act: 'write-off', label: 'Write off', icon: 'edit', danger: true });
    }
  } else if (type === 'BILL' || type === 'EXPENSE') {
    if (open && balance > 0) list.push({ act: 'record-payment', label: 'Record payment', icon: 'wallet', primary: true });
  } else if (type === 'CREDIT_NOTE') {
    if (status !== 'VOID') list.push({ act: 'send', label: 'Send', icon: 'send' });
  }

  // The server voids only an ISSUED document with nothing settled against it.
  if (admin && type !== 'QUOTE' && status === 'ISSUED' && settled <= 0) {
    list.push({ act: 'void', label: 'Void', icon: 'ban', danger: true });
  }
  return list;
}
